import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { FiEdit, FiTrash2, FiStar } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { useAdmin } from "../../../context/AdminContext";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const ManageProjects = () => {
  const { token } = useAdmin();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  // ✅ FETCH (FIXED)
  const fetchProjects = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/projects`);
      setProjects(data);
    } catch (err) {
      console.error(err.response?.data || err);
      alert("Failed to fetch projects.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ DELETE (FIXED)
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project? This cannot be undone."
    );
    if (!confirmDelete) return;

    try {
      setDeletingId(id);

      const authToken = token || localStorage.getItem("adminToken");
      if (!authToken) {
        alert("Admin not authenticated!");
        return;
      }

      await axios.delete(`${API}/api/projects/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      setProjects((prev) => prev.filter((p) => p._id !== id));
    } catch (err) {
      console.error(err.response?.data || err);
      alert(err.response?.data?.message || "Failed to delete project.");
    } finally {
      setDeletingId(null);
    }
  };

  const groupedByCategory = useMemo(() => {
    return projects.reduce((acc, project) => {
      const cat = project.category || "Uncategorized";
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(project);
      return acc;
    }, {});
  }, [projects]);

  const ProjectCard = ({ project }) => {
    const isDeleting = deletingId === project._id;

    return (
      <div className="relative bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-lg hover:scale-[1.02] transition-all duration-200">

        {project.starred && (
          <FiStar className="absolute top-3 right-3 text-yellow-400 w-5 h-5" />
        )}

        <img
          src={project.coverImage || "/placeholder.png"}
          alt={project.name}
          className="w-full h-44 object-cover rounded-xl mb-3"
        />

        <h3 className="text-white font-semibold text-lg">
          {project.name}
        </h3>

        <p className="text-xs text-gray-400 mt-1">
          {project.category}
        </p>

        <p className="text-sm text-gray-300 mt-2 line-clamp-2">
          {project.details}
        </p>

        {project.images?.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto">
            {project.images.map((img, idx) => (
              <img
                key={idx}
                src={img.url}
                alt={`project ${idx + 1}`}
                className="w-10 h-10 object-cover rounded border border-white/20"
              />
            ))}
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={() =>
              navigate(`/admin/manage-projects/edit/${project._id}`)
            }
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
          >
            <FiEdit size={14} />
            Edit
          </button>

          <button
            onClick={() => handleDelete(project._id)}
            disabled={isDeleting}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white rounded-lg text-sm"
          >
            {isDeleting ? (
              <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            ) : (
              <>
                <FiTrash2 size={14} />
                Delete
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-800 p-4 sm:p-6 lg:p-10">

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-8">

        <button
          onClick={() => navigate("/admin/dashboard")}
          className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg backdrop-blur border border-white/10 w-fit"
        >
          ← Back to Dashboard
        </button>

        <h1 className="text-2xl font-bold text-white">
          Manage Projects
        </h1>

        <button
          onClick={() => navigate("/admin/manage-projects/add")}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm"
        >
          + Add Project
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-72 rounded-2xl bg-white/5 animate-pulse"
            />
          ))}
        </div>
      ) : Object.keys(groupedByCategory).length === 0 ? (
        <div className="text-center text-gray-400 mt-20">
          No projects found.
        </div>
      ) : (
        Object.keys(groupedByCategory).map((category) => (
          <div key={category} className="mb-10">

            <h2 className="text-xl font-semibold text-white mb-4 border-l-4 border-green-500 pl-3">
              {category}
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {groupedByCategory[category].map((project) => (
                <ProjectCard key={project._id} project={project} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default ManageProjects;
